import React from 'react';
import { ApplicationView, raw, useController } from '@aha-app/mvc';
import Debug from 'debug';
import WhiteboardController from '../controllers/WhiteboardController';
import type Stylus from '../stylus/Stylus';
import ToolbarButton from './ToolbarButton';
import StylusAssistant from '../stylus/StylusAssistant';

const debug = Debug('whiteboard:ui');

interface StylusToolButtonProps {
  stylus: Stylus;
  active?: boolean;
  onMouseDown?: () => void;
}

const StylusToolButton: React.FC<StylusToolButtonProps> = ({
  stylus,
  active,
  onMouseDown,
}) => {
  const controller = useController(WhiteboardController);
  const { currentStylus } = controller.state;

  const mouseDownHandler = (e: React.MouseEvent) => {
    debug('StylusToolButton mouse down');
    // Prevent focus being taken, which messes with the text editor controls
    // where we want focus to stay in the editor while clicking buttons.
    e.preventDefault();

    onMouseDown?.();

    controller.actionHideContextMenu();

    if (active || controller.isStylusLocked(stylus)) {
      controller.actionSetStylusMenu(null);
    } else {
      controller.actionSetStylusMenu(stylus);
    }

    // The assistant is more of a button than a stylus and we don't want to
    // activate it.
    if (!(stylus instanceof StylusAssistant)) {
      controller.actionSetStylus(stylus);
    } else {
      stylus.activate();
    }

    if (stylus.alwaysLockWhenSelected() && !controller.isStylusLocked(stylus)) {
      controller.actionLockStylus();
    } else {
      controller.actionUnlockStylus();
    }
  };

  const clickHandler = (event: React.MouseEvent) => {
    debug('StylusToolButton click');
    // Reset the stylus when the user clicks on the current stylus button, while
    // still deferring ability to lock stylus to double click.
    if (
      event.detail !== 2 &&
      raw(stylus) !== raw(controller.state.currentStylusMenu!)
    ) {
      controller.actionResetStylus();
    }
  };

  const doubleClickHandler = () => {
    // Double click has the unfortunate side effect of making the menu pop up
    // and then immediately disappear since both clicks fire. The only way to
    // prevent that is to introduce a delay, which is not ideal for the typical
    // experience of a single click.
    debug('StylusToolButton double click');
    controller.actionLockStylus();
    controller.actionSetStylusMenu(null);
  };

  return (
    <ToolbarButton
      active={stylus.isCurrentStylus(currentStylus)}
      locked={controller.isStylusLocked(stylus)}
      onClick={clickHandler}
      onDoubleClick={doubleClickHandler}
      onMouseDown={mouseDownHandler}
      testId={stylus.testid()}
      className={stylus.toolButtonClass?.()}
    >
      {stylus.renderIcon()}
    </ToolbarButton>
  );
};

export default ApplicationView(StylusToolButton);
